/**
 * Barra de filtros del historial de pedidos (ADM-09). Todos se combinan entre
 * si y con el buscador; el control que esta filtrando se pinta en turquesa, igual
 * que en la tabla de productos.
 *
 * Las fechas son dias de Chile y se aplican al elegirlas: un `<input
 * type="date">` solo avisa el cambio cuando la fecha esta completa.
 */
import type { EstadoPedido, OrdenPedidosAdmin, TipoEntrega } from '@bandu/compartido';

import {
  conCambio,
  ENTREGAS,
  ESTADOS_PEDIDO,
  OPCIONES_DOCUMENTO,
  ORDENES_PEDIDOS,
} from '../../utilidades/filtros-pedidos';
import type { FiltrosPedidos as Filtros } from '../../utilidades/filtros-pedidos';
import { FiltroFechas } from './FiltroFechas';
import { FiltroMonto } from './FiltroMonto';

/** Cortos por lo mismo: el chip dice la entrega con su nombre largo. */
const TEXTO_ENTREGA: Record<TipoEntrega, string> = {
  despacho: 'Despacho',
  retiro: 'Retiro',
  coordinar: 'A coordinar',
};

/**
 * Textos cortos para que la barra quepa en una linea; el chip y la tabla usan
 * los largos de `textos-pedido.ts`.
 */
const TEXTO_PAGO: Record<EstadoPedido, string> = {
  pagado: 'Pagado',
  pendiente_pago: 'Sin pagar',
  rechazado: 'Rechazado',
  anulado: 'Anulado',
};

export interface FiltrosPedidosProps {
  filtros: Filtros;
  onCambiar(filtros: Filtros): void;
}

interface FiltroOpcionesProps<T extends string> {
  id: string;
  etiqueta: string;
  valor: T | undefined;
  todos: string;
  opciones: { valor: T; texto: string }[];
  onCambiar(valor: T | undefined): void;
}

/** Un `<select>` cuya primera opcion es "no filtrar". */
function FiltroOpciones<T extends string>({
  id,
  etiqueta,
  valor,
  todos,
  opciones,
  onCambiar,
}: FiltroOpcionesProps<T>) {
  return (
    <div className="filtro">
      <label className="filtro-et" htmlFor={id}>
        {etiqueta}
      </label>
      <select
        id={id}
        className={valor !== undefined ? 'selector activo' : 'selector'}
        value={valor ?? ''}
        onChange={(evento) => {
          const elegida = opciones.find((opcion) => opcion.valor === evento.target.value);
          onCambiar(elegida?.valor);
        }}
      >
        <option value="">{todos}</option>
        {opciones.map((opcion) => (
          <option key={opcion.valor} value={opcion.valor}>
            {opcion.texto}
          </option>
        ))}
      </select>
    </div>
  );
}

export function FiltrosPedidos({ filtros, onCambiar }: FiltrosPedidosProps) {
  const cambiar = (cambio: Partial<Filtros>) => {
    onCambiar(conCambio(filtros, cambio));
  };

  return (
    <section className="filtros-tabla" aria-label="Filtros de pedidos">
      <FiltroOpciones
        id="filtro-estado-pago"
        etiqueta="Pago"
        valor={filtros.estado}
        todos="Todos"
        opciones={ESTADOS_PEDIDO.map((estado) => ({ valor: estado, texto: TEXTO_PAGO[estado] }))}
        onCambiar={(estado) => {
          cambiar({ estado });
        }}
      />

      <FiltroOpciones
        id="filtro-documento-emitido"
        etiqueta="Boleta o factura"
        valor={filtros.documento}
        todos="Todos"
        opciones={OPCIONES_DOCUMENTO}
        onCambiar={(documento) => {
          cambiar({ documento });
        }}
      />

      <FiltroOpciones
        id="filtro-entrega"
        etiqueta="Entrega"
        valor={filtros.tipoEntrega}
        todos="Todas"
        opciones={ENTREGAS.map((entrega) => ({ valor: entrega, texto: TEXTO_ENTREGA[entrega] }))}
        onCambiar={(tipoEntrega) => {
          cambiar({ tipoEntrega });
        }}
      />

      <FiltroOpciones
        id="filtro-documento"
        etiqueta="Tipo de documento"
        valor={filtros.tipoDocumento}
        todos="Todos"
        opciones={[
          { valor: 'boleta', texto: 'Boleta' },
          { valor: 'factura', texto: 'Factura' },
        ]}
        onCambiar={(tipoDocumento) => {
          cambiar({ tipoDocumento });
        }}
      />

      <FiltroFechas
        desde={filtros.desde}
        hasta={filtros.hasta}
        onCambiar={(desde, hasta) => {
          cambiar({ desde, hasta });
        }}
      />

      <FiltroMonto
        minimo={filtros.totalMin}
        maximo={filtros.totalMax}
        onCambiar={cambiar}
      />

      <div className="filtro filtro-orden">
        <label className="filtro-et" htmlFor="filtro-orden-pedidos">
          Ordenar por
        </label>
        <select
          id="filtro-orden-pedidos"
          className="selector"
          value={filtros.orden ?? 'recientes'}
          onChange={(evento) => {
            const orden = evento.target.value as OrdenPedidosAdmin;
            cambiar({ orden: orden === 'recientes' ? undefined : orden });
          }}
        >
          {ORDENES_PEDIDOS.map((opcion) => (
            <option key={opcion.valor} value={opcion.valor}>
              {opcion.texto}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
}
